import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Camera, Loader2, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Convert a File/Blob to an HTMLImageElement (handles large HEIC/HD photos by
 * letting the browser decode them natively).
 */
const fileToImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve({ image: img, dataUrl: reader.result });
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * Render the cropped circle of `imageSrc` onto a canvas sized to `outputSize`,
 * then return a compressed JPEG data URL.
 *
 * - Auto-downsamples HD inputs to outputSize (default 512×512), perfect for avatars.
 * - Always returns a circle by drawing the source onto a transparent canvas
 *   inside a circular clip-path.
 */
const cropImageToDataUrl = async (imageSrc, cropAreaPixels, rotation = 0, outputSize = 512, quality = 0.9) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
    image.src = imageSrc;
  });

  // Working canvas to handle rotation by rendering onto a square that fits the rotated source
  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const bBoxWidth = image.width * cos + image.height * sin;
  const bBoxHeight = image.width * sin + image.height * cos;

  const rotCanvas = document.createElement('canvas');
  rotCanvas.width = bBoxWidth;
  rotCanvas.height = bBoxHeight;
  const rctx = rotCanvas.getContext('2d');
  rctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  rctx.rotate(radians);
  rctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Extract the rectangular crop region from the rotated canvas
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = cropAreaPixels.width;
  cropCanvas.height = cropAreaPixels.height;
  const cctx = cropCanvas.getContext('2d');
  cctx.drawImage(
    rotCanvas,
    cropAreaPixels.x,
    cropAreaPixels.y,
    cropAreaPixels.width,
    cropAreaPixels.height,
    0,
    0,
    cropAreaPixels.width,
    cropAreaPixels.height
  );

  // Downsample to outputSize on a circular canvas
  const finalCanvas = document.createElement('canvas');
  finalCanvas.width = outputSize;
  finalCanvas.height = outputSize;
  const fctx = finalCanvas.getContext('2d');

  // Fill background with solid color (avoids transparency artefacts when saving as JPEG)
  fctx.fillStyle = '#18181B';
  fctx.fillRect(0, 0, outputSize, outputSize);

  // Circular clip
  fctx.save();
  fctx.beginPath();
  fctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
  fctx.closePath();
  fctx.clip();

  fctx.drawImage(cropCanvas, 0, 0, outputSize, outputSize);
  fctx.restore();

  return finalCanvas.toDataURL('image/jpeg', quality);
};

/**
 * ImageCropper, Instagram-style circle crop dialog.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ImageCropper open={open} onOpenChange={setOpen} onCropped={(dataUrl) => ...} />
 *
 * Accepts ANY image size; auto-compresses output to ~512×512 JPEG.
 */
const ImageCropper = ({ open, onOpenChange, onCropped, outputSize = 512, title = 'Crop your photo' }) => {
  const fileInputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    try {
      const { dataUrl } = await fileToImage(file);
      setImageSrc(dataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    } catch (err) {
      console.error(err);
      toast.error('Could not read the image. Try another file.');
    }
  };

  const onCropComplete = useCallback((_areaPercentage, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      toast.error('Pick and position your photo first');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await cropImageToDataUrl(imageSrc, croppedAreaPixels, rotation, outputSize, 0.9);
      onCropped?.(dataUrl);
      onOpenChange(false);
      // Reset for next open
      setTimeout(reset, 200);
    } catch (err) {
      console.error(err);
      toast.error('Could not process the image. Try a different photo.');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-[#18181B] border-zinc-800 text-white max-w-lg"
        data-testid="image-cropper-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">{title}</DialogTitle>
        </DialogHeader>

        {!imageSrc ? (
          <div className="py-8 flex flex-col items-center justify-center gap-4">
            <div className="w-24 h-24 rounded-full bg-zinc-800 flex items-center justify-center">
              <Camera className="w-10 h-10 text-zinc-500" />
            </div>
            <p className="text-zinc-400 text-sm text-center max-w-xs">
              Pick a photo from your device. We&apos;ll let you crop and reposition it.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="hidden"
              data-testid="cropper-file-input"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="bg-[#C0A062] text-black hover:bg-[#a98a52]"
              data-testid="cropper-choose-file-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Choose photo
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative w-full h-72 bg-black rounded-lg overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: { background: '#000' },
                }}
              />
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">Zoom</label>
                <Slider
                  value={[zoom]}
                  min={1}
                  max={4}
                  step={0.05}
                  onValueChange={(v) => setZoom(v[0])}
                  data-testid="cropper-zoom-slider"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  data-testid="cropper-rotate-btn"
                >
                  <RotateCw className="w-4 h-4 mr-2" />
                  Rotate
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 ml-auto"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Pick different photo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onFileChange}
                  className="hidden"
                  data-testid="cropper-file-input"
                />
              </div>
              <p className="text-xs text-zinc-500">
                Drag the photo to reposition, pinch or use the slider to zoom.
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={busy}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={busy || !imageSrc}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52]"
            data-testid="cropper-confirm-btn"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save photo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
